import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userGroupService, type Coverage, type ScopeCatalog, type UserGroup, type UserGroupInput } from '../services/userGroup.service';

export function useUserGroups(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};

  return useQuery<UserGroup[]>({
    queryKey: ['userGroups'],
    queryFn: () => userGroupService.list(),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useUserGroupMemberships(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};

  return useQuery<Record<string, string>>({
    queryKey: ['userGroupMemberships'],
    queryFn: () => userGroupService.memberships(),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useUserGroupCoverage(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};

  return useQuery<Coverage>({
    queryKey: ['userGroupCoverage'],
    queryFn: () => userGroupService.coverage(),
    enabled,
    staleTime: 30 * 1000,
  });
}

/** Metrics and dimensions from Production and Develop, independent of Develop mode. */
export function useScopeCatalog(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};

  return useQuery<ScopeCatalog>({
    queryKey: ['scopeCatalog'],
    queryFn: () => userGroupService.catalog(),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useSaveUserGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, group }: { id?: string; group: UserGroupInput }) =>
      id ? userGroupService.update(id, group) : userGroupService.create(group),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userGroups'] });
      queryClient.invalidateQueries({ queryKey: ['userGroupCoverage'] });
    },
  });
}

export function useDeleteUserGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userGroupService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userGroups'] });
      queryClient.invalidateQueries({ queryKey: ['userGroupCoverage'] });
    },
  });
}
